import { API_BASE } from '../config';
import { getAuthToken } from '../utils/authStorage';

async function authHeaders() {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

function formatApiError(err, fallback) {
  const detail = err?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((item) => {
      if (typeof item === 'string') return item;
      if (item?.msg) {
        const loc = Array.isArray(item.loc)
          ? item.loc.filter((p) => p !== 'body' && p !== 'query').join('.')
          : '';
        return loc ? `${loc}: ${item.msg}` : item.msg;
      }
      return null;
    }).filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string') return detail.message;
    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  }
  return fallback;
}

async function throwIfNotOk(res, fallback) {
  if (res.ok) return;
  const err = await res.json().catch(() => ({}));
  throw new Error(formatApiError(err, fallback));
}

/** List all chemicals with their price per kg */
export async function getChemicalPrices() {
  const res = await fetch(`${API_BASE}/price-calculation/chemical-prices`, {
    headers: await authHeaders(),
  });
  await throwIfNotOk(res, 'Failed to fetch chemical prices');
  return res.json();
}

/** Paginated list of chemicals with their price per kg */
export async function getChemicalPricesPage({ search = '', skip = 0, limit = 20 } = {}) {
  const params = new URLSearchParams();
  if (search && search.trim()) params.set('search', search.trim());
  params.set('skip', String(skip));
  params.set('limit', String(limit));

  const res = await fetch(`${API_BASE}/price-calculation/chemical-prices/paginated?${params}`, {
    headers: await authHeaders(),
  });
  await throwIfNotOk(res, 'Failed to fetch chemical prices');
  return res.json(); // { items, total, page, limit }
}

/** Create or update chemical price per kg */
export async function updateChemicalPrice(chemicalId, data) {
  const res = await fetch(`${API_BASE}/price-calculation/chemical-prices/${chemicalId}`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  await throwIfNotOk(res, 'Failed to update chemical price');
  return res.json();
}

/** Delete current chemical prices for many chemicals (history kept) */
export async function bulkDeleteChemicalPrices(chemicalIds) {
  const res = await fetch(`${API_BASE}/price-calculation/chemical-prices/bulk`, {
    method: 'DELETE',
    headers: await authHeaders(),
    body: JSON.stringify({ chemical_ids: chemicalIds }),
  });
  await throwIfNotOk(res, 'Failed to delete chemical prices');
  return res.json();
}

/** List all products with their saved price per kg */
export async function getProductPrices() {
  const res = await fetch(`${API_BASE}/price-calculation/product-prices`, {
    headers: await authHeaders(),
  });
  await throwIfNotOk(res, 'Failed to fetch product prices');
  return res.json();
}

/** Paginated list of products with their saved price per kg */
export async function getProductPricesPage({ search = '', skip = 0, limit = 20 } = {}) {
  const params = new URLSearchParams();
  if (search && search.trim()) params.set('search', search.trim());
  params.set('skip', String(skip));
  params.set('limit', String(limit));

  const res = await fetch(`${API_BASE}/price-calculation/product-prices/paginated?${params}`, {
    headers: await authHeaders(),
  });
  await throwIfNotOk(res, 'Failed to fetch product prices');
  return res.json(); // { items, total, page, limit }
}

/** Create or update product price per kg (manual) */
export async function updateProductPrice(productId, data) {
  const res = await fetch(`${API_BASE}/price-calculation/product-prices/${productId}`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  await throwIfNotOk(res, 'Failed to update product price');
  return res.json();
}

/** Delete current product prices for many products (history kept) */
export async function bulkDeleteProductPrices(productIds) {
  const res = await fetch(`${API_BASE}/price-calculation/product-prices/bulk`, {
    method: 'DELETE',
    headers: await authHeaders(),
    body: JSON.stringify({ product_ids: productIds }),
  });
  await throwIfNotOk(res, 'Failed to delete product prices');
  return res.json();
}

/** Get calculation breakdown for a product */
export async function getProductPriceBreakdown(productId) {
  const res = await fetch(`${API_BASE}/price-calculation/product/${productId}`, {
    headers: await authHeaders(),
  });
  await throwIfNotOk(res, 'Failed to get breakdown');
  return res.json();
}

/** Calculate with optional overrides (POST body: { overrides: [{ chemical_id, price_per_kg }] } ) */
export async function calculateProductPrice(productId, overrides = null) {
  const res = await fetch(`${API_BASE}/price-calculation/product/${productId}/calculate`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(overrides ? { overrides } : {}),
  });
  await throwIfNotOk(res, 'Failed to calculate');
  return res.json();
}

/** Save calculated product price (sets is_calculated=true) */
export async function saveProductPrice(productId, overrides = null) {
  const res = await fetch(`${API_BASE}/price-calculation/product/${productId}/save`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(overrides ? { overrides } : {}),
  });
  await throwIfNotOk(res, 'Failed to save product price');
  return res.json();
}

/** Get product price history. filters: { product_id?, product_name?, date_from?, date_to?, limit? } */
export async function getProductPriceHistory(filters = {}) {
  const params = new URLSearchParams();
  if (filters.product_id != null) params.set('product_id', filters.product_id);
  if (filters.product_name != null && filters.product_name !== '') params.set('product_name', filters.product_name);
  if (filters.date_from != null && filters.date_from !== '') params.set('date_from', filters.date_from);
  if (filters.date_to != null && filters.date_to !== '') params.set('date_to', filters.date_to);
  if (filters.limit != null) params.set('limit', filters.limit);
  const qs = params.toString();
  const url = `${API_BASE}/price-calculation/product-prices/history${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: await authHeaders() });
  await throwIfNotOk(res, 'Failed to fetch price history');
  return res.json();
}

/** Get chemical price history. filters: { chemical_id?, chemical_name?, date_from?, date_to?, limit? } */
export async function getChemicalPriceHistory(filters = {}) {
  const params = new URLSearchParams();
  if (filters.chemical_id != null) params.set('chemical_id', filters.chemical_id);
  if (filters.chemical_name != null && filters.chemical_name !== '') params.set('chemical_name', filters.chemical_name);
  if (filters.date_from != null && filters.date_from !== '') params.set('date_from', filters.date_from);
  if (filters.date_to != null && filters.date_to !== '') params.set('date_to', filters.date_to);
  if (filters.limit != null) params.set('limit', filters.limit);
  const qs = params.toString();
  const url = `${API_BASE}/price-calculation/chemical-prices/history${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: await authHeaders() });
  await throwIfNotOk(res, 'Failed to fetch chemical price history');
  return res.json();
}
